import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface MlOrderRow {
  id: string;
  ml_order_id: string;
  status: string;
  total_amount_cents: number;
  fees_cents: number;
  shipping_cents: number;
  net_cents: number;
  buyer_nickname: string | null;
  ordered_at: string;
  payload: {
    order_items?: Array<{
      item: { id: string; title: string; seller_sku?: string };
      quantity: number;
      unit_price: number;
    }>;
  } | null;
}

export interface DailySales {
  day: string;        // 'dd/MM'
  orders: number;
  revenueCents: number;
}

export interface TopProduct {
  title: string;
  qty: number;
  revenueCents: number;
}

/** Status que contam como venda (exclui cancelados/inválidos). */
const REVENUE_STATUSES = new Set(['paid', 'confirmed', 'payment_in_process', 'shipped', 'delivered']);

/**
 * Lê os pedidos sincronizados do Mercado Livre (tabela ml_orders, RLS por
 * tenant) e deriva KPIs, série diária de 7 dias e top produtos.
 */
export function useMlOrders(days = 30) {
  const [orders, setOrders] = useState<MlOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
    supabase
      .from('ml_orders')
      .select('id, ml_order_id, status, total_amount_cents, fees_cents, shipping_cents, net_cents, buyer_nickname, ordered_at, payload')
      .gte('ordered_at', since)
      .order('ordered_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setOrders((data as unknown as MlOrderRow[]) ?? []);
        setLoading(false);
      });
  }, [days]);

  const kpis = useMemo(() => {
    const valid = orders.filter((o) => REVENUE_STATUSES.has(o.status));
    const grossCents = valid.reduce((s, o) => s + o.total_amount_cents, 0);
    const netCents = valid.reduce((s, o) => s + o.net_cents, 0);
    const count = valid.length;
    return {
      grossCents,
      netCents,
      count,
      avgTicketCents: count > 0 ? Math.round(grossCents / count) : 0,
    };
  }, [orders]);

  const dailySales: DailySales[] = useMemo(() => {
    const dayKeys: string[] = [];
    const map = new Map<string, DailySales>();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 3600 * 1000);
      const key = d.toISOString().slice(0, 10);
      dayKeys.push(key);
      map.set(key, {
        day: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        orders: 0,
        revenueCents: 0,
      });
    }
    for (const o of orders) {
      if (!REVENUE_STATUSES.has(o.status)) continue;
      const key = o.ordered_at.slice(0, 10);
      const entry = map.get(key);
      if (entry) {
        entry.orders += 1;
        entry.revenueCents += o.total_amount_cents;
      }
    }
    return dayKeys.map((k) => map.get(k)!);
  }, [orders]);

  const topProducts: TopProduct[] = useMemo(() => {
    const map = new Map<string, TopProduct>();
    for (const o of orders) {
      if (!REVENUE_STATUSES.has(o.status)) continue;
      for (const it of o.payload?.order_items ?? []) {
        const key = it.item?.id ?? it.item?.title ?? 'desconhecido';
        const entry = map.get(key) ?? { title: it.item?.title ?? 'Produto', qty: 0, revenueCents: 0 };
        entry.qty += it.quantity ?? 0;
        entry.revenueCents += Math.round((it.unit_price ?? 0) * 100) * (it.quantity ?? 0);
        map.set(key, entry);
      }
    }
    return [...map.values()].sort((a, b) => b.revenueCents - a.revenueCents).slice(0, 5);
  }, [orders]);

  return { orders, loading, error, kpis, dailySales, topProducts };
}
